import { FirmData } from '@/database/baseTypes';
import { AddEditMatterFormData } from '../types';
import { FlattenedMatter } from '@/database/aggregateTypes';
import { AuthenticatedSession } from '@/authenticatedSession/authenticatedSession';
import { createNewMatter } from './createNewMatter';
import { updateExistingMatter } from './updateExistingMatter';
import { getSharePointDriveId } from '../../Documents/microsoft/utils/sharePointDriveUtils';
import { getMicrosoftCredentials } from '@/cloudFunctions/microsoft';
import { telemetry } from '@/lib/telemetry';

export class MissingFieldError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MissingFieldError';
  }
}

export const saveFormData = async (props: {
  authSession: AuthenticatedSession;
  firm: FirmData;
  matter: FlattenedMatter | undefined;
  formData: AddEditMatterFormData;
  onSubmitEventId: string;
}) => {
  const { authSession, firm, matter, formData, onSubmitEventId } = props;
  const usePractistPayments = firm.paymentProvider?.type === 'practist';

  if (formData.clients.length === 0) {
    throw new MissingFieldError('At least one client is required');
  }

  for (const orgContact of formData.clients.filter(
    (c) => c.type === 'organization',
  )) {
    if (orgContact.companyName.trim() === '') {
      throw new MissingFieldError(
        'One or more client companies are missing a name',
      );
    }
  }

  for (const orgContact of formData.adverseParties.filter(
    (c) => c.type === 'organization',
  )) {
    if (orgContact.companyName.trim() === '') {
      throw new MissingFieldError(
        'One or more adverse party companies are missing a name',
      );
    }
  }

  if (matter) {
    telemetry.info({
      message: 'saveFormData: Will call updateExistingMatter',
      extra: {
        matterId: matter?.id ?? 'undefined',
        matterName: formData.matterName ?? 'undefined',
        matterDescription: formData.description ?? 'undefined',
        matterScope: formData.scope ?? 'undefined',
        onSubmitEventId,
      },
    });
    await updateExistingMatter({
      db: authSession.db,
      matter,
      formData,
      onSubmitEventId,
    });
    telemetry.info({
      message: 'saveFormData: Did call updateExistingMatter successfully',
      extra: {
        matterId: matter?.id ?? 'undefined',
        matterName: formData.matterName ?? 'undefined',
        matterDescription: formData.description ?? 'undefined',
        matterScope: formData.scope ?? 'undefined',
        onSubmitEventId,
      },
    });
  } else {
    // Try to get SharePoint access token and drive ID for folder creation
    let accessToken = undefined;
    let driveId = undefined;

    try {
      // Only try to get SharePoint details if we have an MSAL app
      if (firm.emailProvider === 'microsoft') {
        // Get the active account (should be the current user)
        const credentialsResult = await getMicrosoftCredentials();
        switch (credentialsResult.status) {
          case 'success':
            // Get the driveId
            accessToken = credentialsResult.payload.credentials.accessToken;
            driveId = await getSharePointDriveId(
              credentialsResult.payload.credentials.accessToken,
            );
            console.log(
              'Got SharePoint credentials for matter folder creation:',
              { driveId },
            );
            break;
          case 'missing':
            console.error(
              'No Microsoft credentials found, cannot get SharePoint drive ID',
            );
            break;
          case 'error':
            console.error(
              'Failed to get Microsoft credentials for SharePoint drive ID',
              credentialsResult.error,
            );
            break;
        }
      }
    } catch (error) {
      // Log but don't fail if SharePoint connection fails
      console.error('Failed to get SharePoint credentials:', error);
    }

    telemetry.info({
      message: 'saveFormData: Will call createNewMatter',
      extra: {
        matterName: formData.matterName,
        matterDescription: formData.description ?? 'undefined',
        matterScope: formData.scope ?? 'undefined',
        onSubmitEventId,
      },
    });
    await createNewMatter({
      firmDb: authSession.db,
      formData,
      usePractistPayments,
      accessToken,
      driveId,
      onSubmitEventId,
    });
    telemetry.info({
      message: 'saveFormData: Did call createNewMatter successfully',
      extra: {
        matterName: formData.matterName,
        matterDescription: formData.description ?? 'undefined',
        matterScope: formData.scope ?? 'undefined',
        onSubmitEventId,
      },
    });
  }
  return;
};
